import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Card, Stack, Typography } from '@mui/material';
import { OrderStatus } from '@api/Orders/Order';
import OrdersService from '@api/Orders/OrderCtrl';
import ProductCategoriesService from '@api/ProductCategories/ProductCategoryCtrl';
import {
    countOrderDrinks,
    getDrinkCategoryIds,
    getDrinksBonus,
    getMonthWindow,
    readDrinksCounter,
    seedDrinksCounter
} from '@api/Orders/DrinksCounter';
import { useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import { format } from 'date-fns';
import * as locales from 'date-fns/locale';
import drink from '@assets/img/drink.png';
import { currencyFormatter } from '@utils/Numbers';

// ============================================================================
// Bono de bebidas del colaborador. Vive en el cajón de navegación del sidebar.
//
// ⚠️ **Lee UN documento** (el contador `drinks_{sucursal}_{yyyy-MM}` de
// `counters`, que `OrderServ.completeOrder` incrementa con cada pedido). Antes
// leía **todas las órdenes completadas del mes** de la sucursal para pintar este
// número, y como MUI desmonta los hijos del cajón al cerrarlo, lo repetía en
// CADA apertura del menú — la única forma de navegar el panel. El día 8 del mes
// eran ~250-560 documentos por apertura en las sucursales grandes; el día 30,
// más de 2,000. Idea del contador: Irving, 2026-09-09.
//
// **Si el contador no está sembrado, cuenta el mes UNA vez y lo siembra**, en
// vez de mostrar un 0 que no es cierto: este número es dinero del colaborador.
// Después de esa vez, el widget vuelve a costar 1 lectura.
//
// La regla de qué cuenta como bebida y cuánto paga el bono NO vive aquí: está
// en `@api/Orders/DrinksCounter` (12 tests), compartida con quien incrementa.
// ============================================================================

const DrinksSold: React.FunctionComponent = () => {
    const branchOfficeId = useSelector((state: RootReducer) => state.auth.branchOfficeSelected?.id);
    const ordersService = useMemo(() => new OrdersService(), []);
    const productCategoriesService = useMemo(() => new ProductCategoriesService(), []);
    const [drinksCount, setDrinksCount] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);

    // El mes se calcula al montar, NO al cargar el bundle: una tablet que no se
    // recarga cruzaba el fin de mes mostrando el periodo anterior.
    // ⚠️ La ventana es [primero del mes, primero del siguiente): `getOrders`
    // consulta `date < endDate`, así que con el "último día a las 00:00" que se
    // usaba antes el día 30 o 31 quedaba FUERA completo.
    const { firstDay, lastDay } = useMemo(() => {
        const { from, to } = getMonthWindow(new Date());
        return { firstDay: from, lastDay: to };
    }, []);
    /** Solo para el rótulo: el último día real del mes. */
    const ultimoDiaVisible = useMemo(() => new Date(lastDay.getTime() - 86400000), [lastDay]);

    const getPeriod = (date: Date) => `${format(date, 'dd')} de ${format(date, 'LLLL', { locale: locales.es })}`;

    /** Cuenta el mes desde las órdenes. Solo se usa para SEMBRAR el contador. */
    const contarDesdeOrdenes = useCallback(
        async (sucursal: string): Promise<number> => {
            const [orders, categories] = await Promise.all([
                ordersService.getOrders(
                    OrderStatus.Completed,
                    firstDay,
                    lastDay,
                    sucursal,
                    undefined,
                    undefined,
                    false // sin el N+1 del colaborador: aquí solo importan cantidades
                ),
                productCategoriesService.getProductCategories()
            ]);
            const drinkCategoryIds = getDrinkCategoryIds(categories);
            return orders.reduce((total, order) => total + countOrderDrinks(order, drinkCategoryIds), 0);
        },
        [ordersService, productCategoriesService, firstDay, lastDay]
    );

    useEffect(() => {
        if (!branchOfficeId) return;
        let cancelado = false;
        // Carga local y sin loader global: este widget vive en el sidebar y no
        // puede tapar la pantalla mientras resuelve.
        setLoading(true);
        void (async () => {
            try {
                const counter = await readDrinksCounter(branchOfficeId);
                if (cancelado) return;
                if (counter) {
                    setDrinksCount(counter.count || 0);
                    return;
                }
                // Sin sembrar: se paga el conteo del mes UNA vez y queda listo.
                const total = await contarDesdeOrdenes(branchOfficeId);
                if (cancelado) return;
                setDrinksCount(total);
                await seedDrinksCounter(branchOfficeId, total);
            } catch (error) {
                // Sin alerta a propósito: es un widget informativo dentro del
                // menú, y un modal de error al abrir la navegación estorba más
                // de lo que informa. Se queda en el último valor conocido.
                console.error('No se pudo leer el bono de bebidas', error);
            } finally {
                if (!cancelado) setLoading(false);
            }
        })();
        return () => {
            cancelado = true;
        };
    }, [branchOfficeId, contarDesdeOrdenes]);

    return (
        <Stack component={Card} borderRadius={4} m={2} position="relative" direction="row">
            <Stack flex={2} p={2}>
                <Typography variant="h4" color="#895BF1" fontWeight={600}>
                    {loading ? '···' : String(drinksCount).padStart(3, '0')}
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                    Bebidas vendidas
                </Typography>
                <Typography variant="body1">{`Bono acumulado: ${currencyFormatter(getDrinksBonus(drinksCount))}`}</Typography>
                <Typography variant="body2" color="gray">
                    {`Periodo: ${getPeriod(firstDay)} al ${getPeriod(ultimoDiaVisible)}`}
                </Typography>
            </Stack>
            <Box flex={1} />
            <Box component="img" src={drink} position="absolute" right="-10%" top="-10%" height="115%" />
        </Stack>
    );
};

export default DrinksSold;
